import { buildEdgeField, type EdgeField } from '@better-lights/core';
import type { DepthMap } from '../../renderer/types.ts';

/**
 * Edge field for freehand snapping (docs/spec/02 §8.5): photo luminance (gamma-encoded, not
 * linearized) and depth at ≤ 640 px, single-entry cache by (photo, depth) identity.
 */
const LONG_SIDE = 640;
let cache: { photo: ImageBitmap; depth: DepthMap | null; field: EdgeField } | null = null;

export function fieldSize(W: number, H: number): { width: number; height: number; scale: number } {
  const scale = Math.min(1, LONG_SIDE / Math.max(W, H));
  return { width: Math.max(8, Math.round(W * scale)), height: Math.max(8, Math.round(H * scale)), scale };
}

/** Bilinear resize of an 8-bit gray map to 0..1 floats. */
export function resizeGray(src: Uint8Array, sw: number, sh: number, dw: number, dh: number): Float32Array {
  const out = new Float32Array(dw * dh);
  for (let y = 0; y < dh; y++) {
    const fy = Math.max(0, Math.min(sh - 1, ((y + 0.5) * sh) / dh - 0.5));
    const y0 = Math.floor(fy);
    const y1 = Math.min(sh - 1, y0 + 1);
    const wy = fy - y0;
    for (let x = 0; x < dw; x++) {
      const fx = Math.max(0, Math.min(sw - 1, ((x + 0.5) * sw) / dw - 0.5));
      const x0 = Math.floor(fx);
      const x1 = Math.min(sw - 1, x0 + 1);
      const wx = fx - x0;
      const top = src[y0 * sw + x0] * (1 - wx) + src[y0 * sw + x1] * wx;
      const bot = src[y1 * sw + x0] * (1 - wx) + src[y1 * sw + x1] * wx;
      out[y * dw + x] = (top * (1 - wy) + bot * wy) / 255;
    }
  }
  return out;
}

export function edgeFieldFor(photo: ImageBitmap, depth: DepthMap | null): EdgeField {
  if (cache && cache.photo === photo && cache.depth === depth) return cache.field;
  const W = photo.width;
  const H = photo.height;
  const { width, height } = fieldSize(W, H);
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(photo, 0, 0, width, height);
  const px = ctx.getImageData(0, 0, width, height).data;
  const lum = new Float32Array(width * height);
  for (let i = 0; i < lum.length; i++)
    lum[i] = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255;
  const dlum = depth ? resizeGray(depth.data, depth.width, depth.height, width, height) : null;
  const field = buildEdgeField(lum, dlum, width, height, W);
  cache = { photo, depth, field };
  return field;
}
